import { memo } from 'react'
import { FormItem, Label } from '@cargomart/ui'
import { compose, minMax, minMaxFormat, required, trimRequired } from 'forms/validators'
import { FieldNumber } from 'shared/forms/FieldNumber'
import { FieldText } from 'shared/forms/FieldText'
import { OrderCreateRequest } from 'src/schema'

import { TOrderCreateFormData } from '../../types'
import {
  MAX_AMOUNT_GRADE,
  MAX_HEIGHT_GRADE,
  MAX_LENGTH_GRADE,
  MAX_PRICE_GRADE,
  MAX_VOLUME_GRADE,
  MAX_WEIGHT_GRADE,
  MAX_WIDTH_GRADE,
  validationSpecialTotal,
  validationTotal
} from './constants'
import { OrderCargoTotalInformation } from './OrderCargoTotalInformation'
import { OrderCargoTotalWarning } from './OrderCargoTotalWarning'

const name = 'cargoTotal'

interface IProps {
  className?: string
  onOpenScheduler?: () => void
}

export const OrderCargoTotal = memo<IProps>(function OrderCargoTotal({ className, onOpenScheduler }) {
  const labelClassName = '!block truncate pt-2'
  const fieldClassName = 'w-full [&>*:first-child]:!text-on-surface [&>*]:!text-on-surface-variant'

  return (
    <div className={className}>
      <FormItem label={<Label className="pt-2">Описание груза</Label>}>
        <FieldText isShowTooltip className={fieldClassName} name={`${name}.about`} validate={trimRequired} />
      </FormItem>
      <div className="grid grid-cols-1 gap-x-4 3md:grid-cols-4">
        <FormItem label={<Label className={labelClassName}>Самое длинное</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_LENGTH_GRADE}
            name={`${name}.length`}
            postfix="м"
            scale={2}
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMaxFormat(validationForCustomer.length.min, validationForCustomer.length.max, {
                  metric: 'м',
                  name: 'Самое длинное должно быть'
                })
              )(value, allValues)
            }}
          />
        </FormItem>
        <FormItem label={<Label className={labelClassName}>Самое широкое</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_WIDTH_GRADE}
            name={`${name}.width`}
            postfix="м"
            scale={2}
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMaxFormat(validationForCustomer.width.min, validationForCustomer.width.max, {
                  metric: 'м',
                  name: 'Самое широкое должно быть'
                })
              )(value, allValues)
            }}
          />
        </FormItem>
        <FormItem label={<Label className={labelClassName}>Самое высокое</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_HEIGHT_GRADE}
            name={`${name}.height`}
            postfix="м"
            scale={2}
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMaxFormat(validationForCustomer.height.min, validationForCustomer.height.max, {
                  metric: 'м',
                  name: 'Самое высокое должно быть'
                })
              )(value, allValues)
            }}
          />
        </FormItem>
        <FormItem label={<Label className={labelClassName}>Самое тяжёлое</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_WEIGHT_GRADE}
            name={`${name}.maxWeight`}
            postfix="кг"
            scale={2}
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMaxFormat(validationForCustomer.maxWeight.min, validationForCustomer.maxWeight.max, {
                  metric: 'кг',
                  name: 'Самое тяжёлое должно быть'
                })
              )(value, allValues)
            }}
          />
        </FormItem>
      </div>

      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        <FormItem label={<Label className="pt-2">Общий вес</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_WEIGHT_GRADE}
            name={`${name}.weight`}
            postfix="кг"
            scale={3}
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMaxFormat(validationForCustomer.weight.min, validationForCustomer.weight.max, {
                  metric: 'кг',
                  name: 'Общий вес должен быть'
                }),
                () => {
                  if (allValues.cargoType === OrderCreateRequest.cargoType.TOTAL && allValues.cargoTotal) {
                    if (allValues.cargoTotal.weight > allValues.cargoTotal.maxWeight * allValues.cargoTotal.amount) {
                      return 'Общий вес не соответствует количеству мест'
                    }
                  }
                }
              )(value, allValues)
            }}
          />
        </FormItem>
        <FormItem label={<Label className="pt-2">Общий объём</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_VOLUME_GRADE}
            name={`${name}.volume`}
            postfix="м³"
            scale={3}
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMaxFormat(validationForCustomer.volume.min, validationForCustomer.volume.max, {
                  metric: 'м³',
                  name: 'Общий объём должен быть'
                })
              )(value, allValues)
            }}
          />
        </FormItem>
      </div>
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        <FormItem label={<Label className="pt-2">Объявленная ценность</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_PRICE_GRADE}
            min={1}
            name={`${name}.price`}
            postfix="₽"
            scale={2}
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMaxFormat(validationForCustomer.price.min, validationForCustomer.price.max, {
                  metric: '₽',
                  name: 'Объявленная ценность должна быть'
                })
              )(value, allValues)
            }}
          />
        </FormItem>
        <FormItem label={<Label className="pt-2">Всего мест</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_AMOUNT_GRADE}
            min={1}
            name={`${name}.amount`}
            postfix="шт"
            validate={(value, allValues: TOrderCreateFormData) => {
              const validationForCustomer = allValues.customer?.hasSpecialCargoLimits
                ? validationSpecialTotal
                : validationTotal

              return compose(
                required,
                minMax(validationForCustomer.amount.min, validationForCustomer.amount.max, 'шт')
              )(value, allValues)
            }}
          />
        </FormItem>
      </div>
      <OrderCargoTotalInformation onOpenScheduler={onOpenScheduler} />
      <OrderCargoTotalWarning />
    </div>
  )
})
